import { Theme, alpha } from '@mui/material';
import { SystemStyleObject } from '@mui/system';

export const REVO_GRID_HEADER_SIZE = 48;

/** Matches a `size="small"` MUI table row (37px input + cell padding). */
export const REVO_GRID_ROW_SIZE = 52;

export const revoGridTableSx = (theme: Theme): SystemStyleObject<Theme> => ({
  '--revo-grid-font-family': theme.typography.fontFamily ?? 'inherit',
  '--revo-grid-font-size': theme.typography.body2.fontSize as string, // 0.875rem / 14px

  // ---- surfaces & text -----------------------------------------------------
  '--revo-grid-background': theme.palette.background.default,
  '--revo-grid-foreground': theme.palette.text.primary,
  '--revo-grid-text': theme.palette.text.primary,
  '--revo-grid-divider': theme.palette.divider,
  '--revo-grid-shadow': alpha(theme.palette.grey[500], 0.16),
  '--revo-grid-border': theme.palette.divider,

  // ---- header: mirrors MuiTableCell `head` ---------------------------------
  '--revo-grid-header-bg': theme.palette.background.neutral,
  '--revo-grid-header-color': theme.palette.text.secondary,
  '--revo-grid-header-height': `${REVO_GRID_HEADER_SIZE}px`,
  '--revo-grid-header-font-size': theme.typography.body2.fontSize as string,
  '--revo-grid-header-font-weight': `${theme.typography.fontWeightMedium}`,
  '--revo-grid-header-text-transform': 'capitalize',
  '--revo-grid-header-padding': theme.spacing(0, 2),
  '--revo-grid-header-hover-bg': theme.palette.action.hover,
  '--revo-grid-header-border': theme.palette.divider,

  // ---- cells ---------------------------------------------------------------
  '--revo-grid-cell-padding': theme.spacing(0, 2),
  '--revo-grid-cell-border': theme.palette.divider, // horizontal border, between rows
  '--revo-grid-cell-vertical-border': theme.palette.divider, // vertical border, between columns

  // ---- cells ---------------------------------------------------------------
  '--revo-grid-cell-disabled-bg': 'transparent',
  '--revo-grid-row-hover': theme.palette.action.hover,
  '--revo-grid-focused-bg': alpha(theme.palette.primary.main, 0.08),

  // ---- selection / interaction accents -------------------------------------
  '--revo-grid-primary': theme.palette.primary.main,
  '--revo-grid-primary-transparent': alpha(theme.palette.primary.main, 0.9),
  '--revo-grid-selection-bg': alpha(theme.palette.primary.main, 0.08),
  '--revo-grid-selection-border': theme.palette.primary.main,
  '--revo-grid-temporary-selection-border': alpha(theme.palette.primary.main, 0.5),
  '--revo-grid-temporary-range-border': alpha(theme.palette.primary.main, 0.5),
  '--revo-grid-range-handle-bg': theme.palette.primary.main,
  '--revo-grid-autofill-handle-bg': theme.palette.primary.main,
  '--revo-grid-autofill-handle-border': theme.palette.background.default,

  // ---- built-in buttons (used by the filter panel) -------------------------
  '--revo-grid-button-bg': theme.palette.primary.main,
  '--revo-grid-button-text': theme.palette.primary.contrastText,
  '--revo-grid-button-outline-border': theme.palette.divider,
  '--revo-grid-button-outline-text': theme.palette.text.primary,
  '--revo-grid-button-success-bg': theme.palette.success.main,
  '--revo-grid-button-danger-bg': theme.palette.error.main,

  // ---- filter panel (RevoGrid's own popover) -------------------------------
  '--revo-grid-filter-panel-bg': theme.palette.background.default,
  '--revo-grid-filter-panel-border': theme.palette.divider,
  '--revo-grid-filter-panel-divider': theme.palette.divider,
  '--revo-grid-filter-panel-shadow': alpha(theme.palette.grey[500], 0.24),
  '--revo-grid-filter-panel-input-bg': theme.palette.background.neutral,
  '--revo-grid-filter-panel-text': theme.palette.text.primary,
  '--revo-grid-filter-panel-muted-text': theme.palette.text.secondary,
  '--revo-grid-filter-panel-icon': theme.palette.action.active,
  '--revo-grid-filter-panel-icon-active': theme.palette.primary.main,
  '--revo-grid-filter-panel-select-border': theme.palette.divider,
  '--revo-grid-filter-panel-select-border-hover': theme.palette.primary.main,
  '--revo-grid-filter-panel-focus-ring': `0 0 0 3px ${alpha(theme.palette.primary.main, 0.24)}`,

  '& revo-grid': {
    height: '100%',
    border: 'none',
  },
  '& revogr-header .header-rgRow': {
    backgroundColor: theme.palette.background.neutral,
  },
  // Included to add the vertical dividers for column headers
  '& revogr-header .rgHeaderCell': {
    boxShadow: `-1px 0 0 0 ${theme.palette.divider} inset`,
  },

  '& revogr-header .group-rgRow': {
    boxShadow: `inset 0 -1px 0 ${theme.palette.divider}`,
  },
  '& revogr-header .group-rgRow .rgHeaderCell': {
    alignItems: 'center',
    color: theme.palette.text.primary,
    fontWeight: theme.typography.fontWeightMedium,

    borderRight: `1px solid ${theme.palette.divider}`,
  },

  '& revogr-header .rgHeaderCell .sort-indicator': {
    alignItems: 'center',
    marginLeft: theme.spacing(0.5),

    color: theme.palette.text.primary,
    transition: theme.transitions.create(['color', 'opacity'], {
      duration: theme.transitions.duration.shorter,
    }),
  },

  '& revogr-header .rgHeaderCell .sort-indicator i': {
    fontStyle: 'normal',
    lineHeight: 1,
  },
  '& revogr-header .rgHeaderCell .sort-indicator i.sort-off': {
    color: theme.palette.text.disabled,
    opacity: 0,
    '&:after': {
      content: '"↓"',
      fontSize: '13px',
    },
  },
  '& revogr-header .rgHeaderCell.sortable:hover .sort-indicator i.sort-off': {
    opacity: 1,
  },

  '& revogr-header .rgHeaderCell .sort-indicator .sort-order-index': {
    color: theme.palette.text.secondary,
  },

  '& revogr-header .rgHeaderCell .sm-header-content': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    height: '100%',
    minWidth: 0,
  },

  '& revogr-header .rgHeaderCell .sm-header-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  '& revogr-header .rgHeaderCell .sm-filter-button': {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 24,
    height: 24,
    padding: 0,
    border: 0,
    borderRadius: '50%',
    background: 'none',
    color: theme.palette.action.active,
    cursor: 'pointer',
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '& svg': { width: 11, height: 11 },
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  '& revogr-header .rgHeaderCell:hover .sm-filter-button, & revogr-header .rgHeaderCell .sm-filter-button:focus-visible':
    {
      opacity: 1,
    },
  '& revogr-header .rgHeaderCell .sm-filter-button.active': {
    opacity: 1,
    color: theme.palette.primary.main,
  },

  '& .sm-checkbox': {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '50%',
    color: theme.palette.action.active,
    transition: theme.transitions.create(['background-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '& svg': { width: 20, height: 20 },
    '&:hover, &:focus-within': {
      backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.hoverOpacity),
    },
    '&.checked': { color: theme.palette.primary.main },
  },
  '& .sm-checkbox .sm-checkbox-input': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    opacity: 0,
    cursor: 'pointer',
  },

  '& .sm-switch': {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    width: 34,
    height: 20,
    '&.disabled': { opacity: 0.48 },
  },
  '& .sm-switch .sm-switch-input': {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    opacity: 0,
    cursor: 'pointer',
    '&:disabled': { cursor: 'default' },
  },
  '& .sm-switch .sm-switch-track': {
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    borderRadius: 14,
    backgroundColor: alpha(theme.palette.grey[500], 0.48),
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .sm-switch .sm-switch-thumb': {
    position: 'absolute',
    top: 3,
    left: 3,
    pointerEvents: 'none',
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: theme.palette.common.white,
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.shortest,
    }),
  },
  '& .sm-switch.checked .sm-switch-track': {
    backgroundColor: theme.palette.primary.main,
  },
  // 34 (track) - 14 (thumb) - 3 (left inset) - 3 (right inset)
  '& .sm-switch.checked .sm-switch-thumb': {
    transform: 'translateX(14px)',
  },

  '& revogr-data .rgRow:hover .rgCell': {
    backgroundColor: theme.palette.action.hover,
  },
  '& revogr-edit input, & revogr-edit .edit-input-wrapper input': {
    font: 'inherit',
    fontSize: theme.typography.body2.fontSize,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(0, 2),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: `${theme.shape.borderRadius}px`,
    outline: 'none',
    boxSizing: 'border-box',
  },
});
